import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { mergeRefs } from "react-merge-refs";
import { Container, InnerTextArea, Props } from "./TextArea.Parts";

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  Props & ComponentPropsWithoutRef<"textarea">
>(
  (
    {
      label,
      id: idProp,
      hasError,
      disabled,
      emulateFocus,
      initialHeight,
      maxHeight,
      width,
      placeholder,
      defaultValue,
      value,
      className,
      style,
      onChange,
      onBlur,
      ...textareaProps
    },
    ref,
  ) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const generatedId = useId();
    const id = idProp || generatedId;

    const [hasContent, setHasContent] = useState(
      Boolean(defaultValue) || Boolean(value) || Boolean(placeholder),
    );

    const adjustHeight = () => {
      const textarea = textAreaRef.current;
      if (textarea) {
        // Reset the height to 'auto' to let the browser find the natural height
        textarea.style.height = "auto";
        // Set the height to the calculated scrollHeight. This makes it grow.
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    // This effect handles the initial render and programmatic value changes
    useEffect(() => {
      const hasInitialContent =
        Boolean(textAreaRef.current?.value) ||
        Boolean(textAreaRef.current?.defaultValue) ||
        Boolean(placeholder);

      setHasContent(hasInitialContent);

      if (hasInitialContent) adjustHeight();
    }, [
      textAreaRef.current?.value,
      textAreaRef.current?.defaultValue,
      placeholder,
    ]);

    useEffect(() => {
      if (emulateFocus && !disabled) {
        textAreaRef.current?.focus();
      }
    }, [emulateFocus, disabled]);

    return (
      <Container
        $hasContent={hasContent}
        $hasError={hasError}
        $disabled={disabled}
        $emulateFocus={emulateFocus}
        $initialHeight={initialHeight}
        $maxHeight={maxHeight}
        $width={width}
        onClick={() => {
          !disabled && textAreaRef.current?.focus();
        }}
        className={className}
        style={style}
      >
        <label htmlFor={id}>{label}</label>
        <InnerTextArea
          rows={initialHeight && parseFloat(initialHeight) < 6 ? 1 : undefined}
          ref={mergeRefs([ref, textAreaRef])}
          $disabled={disabled}
          $hasContent={hasContent}
          $emulateFocus={emulateFocus}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={(e) => {
            setHasContent(e.target.value !== "" || !!placeholder);
            adjustHeight();
            onChange?.(e);
          }}
          onBlur={(e) => {
            if (emulateFocus && !disabled) {
              textAreaRef.current?.focus();
            }
            onBlur?.(e);
          }}
          id={id}
          {...textareaProps}
        />
      </Container>
    );
  },
);
